import type { PlaceItem, PlaceSearchContextForSave, PlaceToIdeaPayload } from '../types/place.types';

export function newPlaceExternalId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `place-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export type PlaceStopDateTimes = {
  start_date_time: string;
  end_date_time: string;
};

export function buildPlaceToIdeaPayload(
  place: PlaceItem,
  searchContext: PlaceSearchContextForSave,
  stopDates?: PlaceStopDateTimes | null
): PlaceToIdeaPayload {
  const external_id = place.id?.trim() || newPlaceExternalId();
  const discover_payload = JSON.stringify({
    v: 1,
    source: 'discover_place_search',
    location: searchContext.locationLabel,
    place_type: searchContext.placeType,
    place_type_label: searchContext.placeTypeLabel,
    radius_meters: searchContext.radiusMeters,
    rank: searchContext.rankLabel,
    place_id: place.id ?? null,
  });

  const payload: PlaceToIdeaPayload = {
    name: place.name?.trim() || 'Place',
    place_type: searchContext.placeType.trim() || 'store',
    address: place.address?.trim() || '',
    short_address: place.short_address?.trim() || place.address?.trim() || '',
    latitude: place.latitude ?? 0,
    longitude: place.longitude ?? 0,
    description: place.description?.trim() || '',
    rating: place.rating ?? 0,
    user_ratings_total: place.user_ratings_total ?? 0,
    phone_number: place.phone_number?.trim() || '',
    website: place.website?.trim() || '',
    pet_friendly: place.pet_friendly === true,
    good_for_children: place.good_for_children === true,
    good_for_group: place.good_for_group === true,
    is_open_now: place.is_open_now === true,
    photo_url: place.photo_url?.trim() || '',
    distance_meters: searchContext.radiusMeters,
    external_id,
    discover_payload,
  };

  if (stopDates?.start_date_time && stopDates?.end_date_time) {
    payload.start_date_time = stopDates.start_date_time;
    payload.end_date_time = stopDates.end_date_time;
  }

  return payload;
}

export function tripDateRangeFromPlacePayload(
  body: PlaceToIdeaPayload
): { start_date: string; end_date: string } | undefined {
  const days = [body.start_date_time, body.end_date_time]
    .map(value => value?.trim().slice(0, 10))
    .filter((value): value is string => Boolean(value));

  if (days.length === 0) return undefined;

  const sorted = [...days].sort();
  return { start_date: sorted[0], end_date: sorted[sorted.length - 1] };
}

export function validatePlaceStopWithinTrip(
  tripStart: string,
  tripEnd: string,
  startDateTime: string,
  endDateTime: string
): string | null {
  const tripStartDay = tripStart.trim().slice(0, 10);
  const tripEndDay = tripEnd.trim().slice(0, 10);
  if (!tripStartDay || !tripEndDay) {
    return 'This trip has no start or end date. Add trip dates before saving a stop.';
  }

  const stopStartDay = startDateTime.trim().slice(0, 10);
  const stopEndDay = endDateTime.trim().slice(0, 10);
  if (!stopStartDay || !stopEndDay) {
    return 'Stop start and end date are required.';
  }

  if (stopStartDay < tripStartDay || stopEndDay > tripEndDay) {
    return `Stop dates must fall within the trip (${tripStartDay} – ${tripEndDay}).`;
  }

  return null;
}
